<template>
  <div>
    <BaseCard card-name="Physician Schedule" :spinnerOn="isLoading" class="base-container">
      <div v-if="!isLoading">
        <div class="save-button-caregiver-div">
          <button class="primary-filled-button" @click="handleSaveCaregiverData()"
            :disabled="isSaveButtonDisabled">Save</button>
        </div>
        <div class="custom-physicianScheduleTable-class">
          <DataTable v-if="!physicianScheduleData?.length || !departments?.length && !isLoading" :value="[]">
            <template v-if="!physicianScheduleData?.length && !isLoading" #empty>
              <p class="no-records-class"> No Tumor Board Scheduled</p>
            </template>
            <template v-else-if="!departments?.length && !isLoading" #empty>
              <p class="no-records-class"> No Specialties available</p>
            </template>
            <Column field="date" header="DATE"></Column>
            <Column field="tb_name" header="TUMOR BOARD">>
            </Column>
          </DataTable>
          <DataTable v-else :value="physicianScheduleData" scrollable scrollHeight="70vh" tableStyle="max-width: 200rem"
            @page="handlePageChange">
            <Column field="serialNumber" header="" sortable></Column>
            <Column field="date" header="Date">
              <template #body="{ data }">
                {{ data.date }} {{ data.time.slice(0, -3) }}
              </template>
            </Column>
            <Column field="tb_name" header="Tumor Board" sortable>
            </Column>
            <template v-for="(department, index) in departments" :key="department.department_id">
              <Column :field="department.department_name" :header="department.department_name">
                <template #body="rowData">
                  <MultiSelectDropdown :department="department.department_name"
                    :defaultOptions="calculateDefaultOptions(physicianScheduleData[rowData.data.physicianScheduleDataIndex].departments[index].caregivers, physicianScheduleData[rowData.data.physicianScheduleDataIndex])"
                    :options="calculateOptionsValues(physicianScheduleData[rowData.data.physicianScheduleDataIndex].departments[index].caregivers, physicianScheduleData[rowData.data.physicianScheduleDataIndex])"
                    @selected-values-changed="onSelectedValuesChanged" />
                </template>
              </Column>
            </template>
            <Column field="tb_name" header="External User">
              <template #body="rowData">
                <button class="primary-outlined-button invite-button" @click="openInviteModal(rowData.data)">
                  <span class="invite-text">Invite</span>
                  <span class="material-symbols-outlined file-icon">send</span>
                </button>
              </template>
            </Column>
          </DataTable>
          <Paginator v-if="physicianScheduleData?.length && departments?.length" :first="firstPage" :rows="numberOfRows"
            :totalRecords="physicianScheduleDataCount" @page="handlePageChange">
          </Paginator>

        </div>
      </div>
      <div>
      </div>
      <ExternalUsersModal v-if="isInviteModalVisible" :tumorBoardName="selectedTumorboardName"
        :tumorBoardDate="selectedTumorboardDate" :tumorBoardId="selectedTumorboardId"
        @close-modal="isInviteModalVisible = false" />
    </BaseCard>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { defineEmits, onMounted, ref, watch } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Paginator from 'primevue/paginator'
import BaseCard from '@/ux3/cards/BaseCard/BaseCard.vue'
import MultiSelectDropdown from './MultiSelectDropdown.vue'
import ExternalUsersModal from '@/ux3/components/ExternalUsers/ExternalUsersModal.vue'
import { getPhysicianScheduleData } from './getPhysicianScheduleData.js'
import { getPhysicianScheduleDataCount } from './getPhysicianScheduleDataCount.js'
import { patchPhysicianScheduleData } from './patchPhysicianScheduleData.js'
import { sendMailPhysicianSchedule } from './sendMailPhysicianSchedule.js'
import { showToast } from '@/utils/utils.js'

const physicianScheduleData = ref([])
const physicianScheduleDataCount = ref(0)
const departments = ref([])
const isLoading = ref(true)
const selectedCaregiverData = ref([])
const caregiverPayload = ref([])
const isSaveButtonDisabled = ref(true)
const currentPage = ref(1)
const selectedPage = ref(1)
const isInviteModalVisible = ref(false)
const firstPage = ref(0)
const numberOfRows = ref(6)
const rowsToBeSkipped = ref(0)
const pageChangeDirectionNumber = ref(0)
const selectedTumorboardName = ref('')
const selectedTumorboardDate = ref('')
const selectedTumorboardId = ref('')

const emit = defineEmits(['close-modal', 'form-change', 'reset-confirmation', 'send-external-invite'])

onMounted(async () => {
  const physicianScheduleCountResponse = await getPhysicianScheduleDataCount()
  physicianScheduleDataCount.value = physicianScheduleCountResponse.count
  await reloadPhysicianScheduleData()
})

watch(isSaveButtonDisabled, () => {
  if (isSaveButtonDisabled.value) {
    emit('reset-confirmation')
  } else {
    emit('form-change')
  }
})

const openInviteModal = (data) => {
  selectedTumorboardName.value = data.tb_name
  selectedTumorboardDate.value = data.date
  selectedTumorboardId.value = data.tb_id
  isInviteModalVisible.value = true
}

const reloadPhysicianScheduleData = async () => {
  const physicianScheduleResponse = await getPhysicianScheduleData(rowsToBeSkipped.value, numberOfRows.value)
  physicianScheduleData.value = physicianScheduleResponse.map((item, index) => {
    return { ...item, serialNumber: index + 1 + rowsToBeSkipped.value, physicianScheduleDataIndex: index }
  })
  departments.value = physicianScheduleData.value[0]?.departments || []
  isLoading.value = false
}

const calculateDefaultOptions = (caregivers, dateDetails) => {
  const defaultOptions = caregivers
    .filter((caregiver) => caregiver.is_selected)
    .map((caregiver) => ({
      label: caregiver.caregiver_name,
      caregiverId: caregiver.caregiver_id,
      date: dateDetails.date,
      tbId: dateDetails.tb_id,
      is_selected: caregiver.is_selected,
    }))
  return defaultOptions
}

const calculateOptionsValues = (caregivers, dateDetails) => {
  const options = caregivers
    .map((caregiver) => ({
      label: caregiver.caregiver_name,
      caregiverId: caregiver.caregiver_id,
      date: dateDetails.date,
      tbId: dateDetails.tb_id,
      is_selected: caregiver.is_selected,
    }))
  return options
}

const onSelectedValuesChanged = (selectedValues) => {
  // we compute this index to see if we've already started collecting
  // newly selected/deselected caregivers for a given tb_id/date
  // -1 means we have not yet
  const existingEntryIndex = selectedCaregiverData.value.findIndex(
    (entry) =>
      entry.date === selectedValues[0].date &&
      entry.tb_id === selectedValues[0].tbId,
  )
  if (existingEntryIndex !== -1) {
    // see if this cargiver is already in the selected
    const existingCaregiverIndex = selectedCaregiverData.value[existingEntryIndex].caregivers.findIndex(
      (caregiver) => caregiver.caregiver_id === selectedValues[0].caregiverId,
    )
    // if we've found this caregiver already
    if (existingCaregiverIndex !== -1) {
      // do a splice to remove from cargivers array
      selectedCaregiverData.value[existingEntryIndex].caregivers.splice(existingCaregiverIndex, 1)

      // we should only go to true if EVERY caregivers array is empty
      // these arrays represent changes to the caregivers dropdown menus
      const changedSelections = selectedCaregiverData.value.some((caregiversBoard) => caregiversBoard.caregivers.length !== 0)
      if (!changedSelections) {
        isSaveButtonDisabled.value = true
        selectedCaregiverData.value = []
      } else {
        isSaveButtonDisabled.value = false
      }
    } else {
      selectedCaregiverData.value[existingEntryIndex].caregivers.push({
        caregiver_id: selectedValues[0].caregiverId,
        is_selected: selectedValues[0].is_selected,
      })
      isSaveButtonDisabled.value = false
    }
  } else {
    selectedCaregiverData.value.push({
      date: selectedValues[0].date,
      tb_id: selectedValues[0].tbId,
      caregivers: [{ caregiver_id: selectedValues[0].caregiverId, is_selected: selectedValues[0].is_selected }],
    })
    isSaveButtonDisabled.value = false
  }
}

const handleSaveCaregiverData = async () => {
  caregiverPayload.value = { caregiver_schedules: selectedCaregiverData.value }
  const response = await patchPhysicianScheduleData(caregiverPayload.value)
  response.detail ? showToast('error', 'Error saving the physicians for the schedules') : showToast('success', 'Changes Saved')
  if (response.detail) { console.log(response.detail[0].msg) }
  else {
    selectedCaregiverData.value = []
    isSaveButtonDisabled.value = true
    await reloadPhysicianScheduleData()
    const mailPayload = caregiverPayload.value.caregiver_schedules.map(item => ({
      date: item.date,
      tb_id: item.tb_id,
    }))
    await sendMailPhysicianSchedule(mailPayload)
    emit('reset-confirmation')
    showToast('success', 'Invite triggered for updated schedules')
  }
}

const handlePageChange = async (event) => {
  pageChangeDirectionNumber.value = event.first
  if (selectedCaregiverData.value.length) {
    selectedPage.value = event.page + 1
  } else {
    currentPage.value = event.page + 1
    rowsToBeSkipped.value = pageChangeDirectionNumber.value
    await reloadPhysicianScheduleData()
  }
}
</script>

<style>
.base-container {
  z-index: 99;
}

.custom-physicianScheduleTable-class .p-datatable .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
}

.custom-physicianScheduleTable-class .p-datatable .p-datatable-tbody>tr>td {
  color: var(--Black);
  font-size: 12px;
  font-weight: 400;
}

.custom-physicianScheduleTable-class .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.save-button-caregiver-div {
  text-align: right;
  margin-right: 1rem;
}

.no-records-class {
  color: var(--Black);
  font-size: 1.125rem;
  font-weight: 500;
  text-align: center;
}

.invite-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invite-text {
  margin-left: 1.5rem;
}
</style>
